import { useRef, useState, useEffect } from "react";
import { Mic, Square, Loader2, Sparkles, ChevronDown, ChevronUp, Clock, AudioLines } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { createRecorder, extForMime, logRecorderError, micErrorMessage } from "../../lib/recorder";
// Graba la consulta, la transcribe (Whisper) y estructura la nota con IA.
export default function ConsultRecorder({ patientId, onStructured }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);
  const [usage, setUsage] = useState(null);
  const mediaRec = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const secondsRef = useRef(0);

  const loadUsage = () => api.get("/transcription-usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => { loadUsage(); }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = createRecorder(stream);
      mr.onerror((e) => logRecorderError("recorder onerror", e, mr));
      mr.onstop = async (blob) => {
        stream.getTracks().forEach((t) => t.stop());
        const dur = secondsRef.current;
        if (blob.size > 800) await processAudio(blob, dur);
        else toast.error("Grabación demasiado corta.");
      };
      mr.start();
      mediaRec.current = mr;
      setRecording(true);
      setSeconds(0);
      secondsRef.current = 0;
      timer.current = setInterval(() => setSeconds((s) => { secondsRef.current = s + 1; return s + 1; }), 1000);
    } catch (e) {
      logRecorderError("startRecording", e, mediaRec.current);
      toast.error(micErrorMessage(e));
    }
  };

  const stopRecording = () => {
    if (mediaRec.current) mediaRec.current.stop();
    if (timer.current) clearInterval(timer.current);
    setRecording(false);
  };

  const structureNote = async (text) => {
    const res = await api.post("/ai/consult-note", { transcript: text, patient_id: patientId });
    const p = res.data.parsed;
    if (p) {
      onStructured?.(p);
      if (res.data.fallback) toast.warning("No se pudo separar por apartados; pusimos el dictado en Padecimiento actual para que lo ajustes.");
      else toast.success("Nota estructurada desde la conversación. Revisa y ajusta.");
    } else {
      toast.error("No se pudo estructurar la nota, pero la transcripción está lista.");
    }
  };

  const retryStructure = async () => {
    if (!transcript) return;
    setBusy(true);
    try { await structureNote(transcript); }
    catch { toast.error("No se pudo redactar. Intenta de nuevo."); }
    finally { setBusy(false); }
  };

  const processAudio = async (blob, durationSec = 0) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `consulta.${extForMime(blob.type)}`, { type: blob.type || "audio/webm" }));
      fd.append("duration_sec", String(durationSec || 0));
      const tr = await api.post("/ai/voice", fd);
      const text = (tr.data.text || "").trim();
      if (!text) { toast.error("No se escuchó nada. Intenta de nuevo."); setBusy(false); return; }
      setTranscript(text);
      setShowTranscript(true);
      await structureNote(text);
      loadUsage();
    } catch (e) {
      if (e?.response?.status === 429) toast.error(e.response.data?.detail || "Alcanzaste el límite de minutos de dictado de este mes.");
      else toast.error("No se pudo procesar la grabación.");
      loadUsage();
    } finally {
      setBusy(false);
    }
  };

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="rounded-2xl border border-brand-terracotta/25 bg-brand-terracotta/[0.04] p-4" data-testid="consult-recorder">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-brand-ink/70">
          <AudioLines size={14} className="text-brand-terracotta" /> Dictado de historia clínica
        </span>
        <span data-testid="clinped-voice-badge" className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full bg-gradient-to-r from-brand-terracotta to-brand-sageDark text-white">
          <AudioLines size={13} /> ClinPed Voice
        </span>
      </div>
      <div className="flex items-center gap-3">
        {!recording ? (
          <button type="button" data-testid="rec-start-btn" onClick={startRecording} disabled={busy || (usage && !usage.unlimited && usage.remaining_min <= 0)}
            className="flex items-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-all disabled:opacity-60 disabled:cursor-not-allowed">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Mic size={16} />}
            {busy ? "Procesando…" : "Grabar consulta"}
          </button>
        ) : (
          <button type="button" data-testid="rec-stop-btn" onClick={stopRecording}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-all animate-pulse">
            <Square size={15} /> Detener ({mm}:{ss})
          </button>
        )}
        <div className="flex items-start gap-1.5 text-[11px] text-brand-ink/60 leading-tight">
          <Sparkles size={13} className="mt-0.5 shrink-0 text-brand-terracotta" />
          <span>Graba la conversación y la IA transcribe y llena la historia clínica por apartados (padecimiento, exploración, antecedentes). El diagnóstico y tratamiento los defines tú. Siempre revisa antes de guardar.</span>
        </div>
      </div>

      {usage && !usage.unlimited && (
        <div data-testid="transcription-usage" className={`mt-3 flex items-center gap-1.5 text-[11px] font-semibold ${usage.remaining_min <= 0 ? "text-red-600" : usage.remaining_min <= 30 ? "text-amber-600" : "text-brand-ink/55"}`}>
          <Clock size={12} />
          <span>{usage.remaining_min <= 0
            ? `Límite mensual alcanzado (${usage.limit_min} min). Se renueva el próximo mes.`
            : `Te quedan ${usage.remaining_min} de ${usage.limit_min} min de dictado este mes.`}</span>
        </div>
      )}

      {transcript && (
        <div className="mt-3">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => setShowTranscript((v) => !v)} data-testid="rec-toggle-transcript"
              className="flex items-center gap-1 text-xs font-semibold text-brand-ink/70">
              {showTranscript ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Transcripción
            </button>
            <button type="button" onClick={retryStructure} disabled={busy} data-testid="rec-retry-structure"
              className="flex items-center gap-1.5 text-xs font-semibold text-brand-terracotta hover:text-brand-terracottaDark disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Reintentar redacción
            </button>
          </div>
          {showTranscript && (
            <p data-testid="rec-transcript" className="mt-1.5 text-xs text-brand-ink/70 bg-white/70 rounded-xl p-3 max-h-40 overflow-y-auto whitespace-pre-wrap">{transcript}</p>
          )}
        </div>
      )}
    </div>
  );
}
